'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ArrowRight, Sparkles } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const announcement = {
  eyebrow: 'New at ReACH',
  mobileBannerText: 'New ReACH updates — tap to learn more',
  bannerText:
    'Explore updates to appointments, patient records, and mental health support at ReACH.',
  dialogTitle: 'What is new at ReACH?',
  dialogDescription:
    'We are improving the ReACH digital experience so patients can access care, records, appointments, and support more smoothly across the website.',
  points: [
    'Easier access to patient records',
    'Clearer booking and appointment flows',
    'Improved information across care pathways',
  ],
  primaryCta: {
    label: 'Book a session',
    href: '/book',
  },
  secondaryCta: {
    label: 'View patient records',
    href: '/patient',
  },
}

const AnnouncementBanner = () => {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const closeDialog = () => {
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className="fixed inset-x-0 top-0 z-[60] h-9 border-b border-cyan-200/70 bg-cyan-50/95 shadow-sm backdrop-blur-md sm:h-10">
        <DialogTrigger asChild>
          <button
            type="button"
            className="group flex h-full w-full items-center justify-center px-4 text-xs text-zinc-900 transition hover:bg-cyan-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-inset sm:text-sm"
            aria-label="Open ReACH announcement"
          >
            <span className="mx-auto flex w-full max-w-[1275px] items-center justify-center gap-2 overflow-hidden">
              <span className="hidden shrink-0 items-center gap-1.5 font-semibold text-cyan-800 sm:inline-flex">
                <Sparkles className="h-3.5 w-3.5" />
                {announcement.eyebrow}
              </span>

              <span className="truncate sm:hidden">
                {announcement.mobileBannerText}
              </span>

              <span className="hidden truncate sm:inline">
                {announcement.bannerText}
              </span>

              <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        </DialogTrigger>
      </div>

      <DialogContent className="w-[calc(100%-1rem)] max-w-xl rounded-3xl border-zinc-200 bg-white p-0 shadow-2xl sm:w-full">
        <div className="overflow-hidden rounded-3xl">
          <div className="border-b border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 px-6 py-6">
            <DialogHeader className="space-y-3 text-left">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-200 bg-white/80 px-3 py-1 text-xs font-medium text-cyan-800">
                <Sparkles className="h-3.5 w-3.5" />
                {announcement.eyebrow}
              </div>

              <DialogTitle className="text-2xl font-semibold leading-tight tracking-tight text-zinc-950">
                {announcement.dialogTitle}
              </DialogTitle>

              <DialogDescription className="text-base leading-7 text-zinc-600">
                {announcement.dialogDescription}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-5 px-6 py-6">
            <ul className="space-y-3">
              {announcement.points.map((point) => (
                <li
                  key={point}
                  className="flex gap-3 text-sm leading-6 text-zinc-700"
                >
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <DialogFooter className="gap-2 sm:justify-start">
              <Button
                asChild
                className="rounded-full bg-cyan-400 px-6 text-black hover:bg-cyan-500"
              >
                <Link href={announcement.primaryCta.href} onClick={closeDialog}>
                  {announcement.primaryCta.label}
                </Link>
              </Button>

              <Button asChild variant="outline" className="rounded-full">
                <Link
                  href={announcement.secondaryCta.href}
                  onClick={closeDialog}
                >
                  {announcement.secondaryCta.label}
                </Link>
              </Button>

              <DialogClose asChild>
                <Button variant="ghost" className="rounded-full">
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AnnouncementBanner
